import type {DelvauxCatalog} from "#pricing/catalogs/delvaux/catalog"
import type {PriceGraphData} from "#pricing/catalogs/graph-helpers"
import {createPriceContainer, createRuleConditionedPriceNodes} from "#pricing/catalogs/graph-helpers"
import type {ConfigVariantRule} from "#pricing/nodes/config-variant-condition"
import {ConfigVariantCondition} from "#pricing/nodes/config-variant-condition"
import {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"

function variantNameCondition(configGroup: ConfigurationGroupNode, valueContains: string, valueNotContains?: string[]) {
    const rules: ConfigVariantRule[] = [
        {
            field: "variantName",
            operator: "contains",
            value: valueContains,
            caseInsensitive: true,
        },
    ]

    valueNotContains?.forEach((value) => {
        rules.push({
            field: "variantName",
            operator: "notContains",
            value,
            caseInsensitive: true,
        })
    })
    return new ConfigVariantCondition({parameters: {variant: configGroup, rules}})
}

let Collection = new ConfigurationGroupNode({
    parameters: {
        groupId: "silhouette",
        groupName: "Silhouette",
        currentVariant: {groupId: "silhouette", groupName: "Silhouette", variantId: "ed02d7c3-f543-4989-934d-bc2480e961ad", variantName: "Brillant PM"},
        allVariants: [],
    },
})

let Colors = new ConfigurationGroupNode({
    parameters: {
        groupId: "colors",
        groupName: "Colors",
        currentVariant: {groupId: "colors", groupName: "Colors", variantId: "9e3a8fd5-c6c9-4c3c-a023-f1b995bf70e6", variantName: "Ostrich"},
        allVariants: [],
    },
})

const rules: ConfigVariantRule[][] = [
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Ostrich",
            caseInsensitive: true,
        },
        {
            field: "variantName",
            operator: "notContains",
            value: "Pourss",
            caseInsensitive: true,
        },
    ],
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Pourssi",
            caseInsensitive: true,
        },
    ],
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Porosus",
            caseInsensitive: true,
        },
    ],
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Alligator",
            caseInsensitive: true,
        },
    ],
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Lizard",
            caseInsensitive: true,
        },
        {
            field: "variantName",
            operator: "notContains",
            value: "Ceramica",
            caseInsensitive: true,
        },
    ],
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Lizard",
            caseInsensitive: true,
        },
        {
            field: "variantName",
            operator: "contains",
            value: "Ceramica",
            caseInsensitive: true,
        },
    ],
    [
        {
            field: "variantName",
            operator: "contains",
            value: "Niloticus",
            caseInsensitive: true,
        },
    ],
]

export function createMtoPriceGraph(catalog: DelvauxCatalog): PriceGraphData {
    const result: PriceGraphData = {
        amountGroups: [],
        pricedItemNodes: [],
        priceContainers: [],
    }

    const brillantPmAssets = createRuleConditionedPriceNodes(catalog, "Brillant P", 6, {configGroup: Colors, rules})
    const brillantPmContainer = createPriceContainer("Brillant PM", "Brillant PM", brillantPmAssets, variantNameCondition(Collection, "Brillant P"))

    const brillantMiniAssets = createRuleConditionedPriceNodes(catalog, "Brillant Mini", 7, {configGroup: Colors, rules})
    const brillantMiniContainer = createPriceContainer("Brillant Mini", "Brillant Mini", brillantMiniAssets, variantNameCondition(Collection, "Brillant Mini"))

    const brillantToyAssets = createRuleConditionedPriceNodes(catalog, "Brillant Toy", 3, {configGroup: Colors, rules})
    const brillantToyContainer = createPriceContainer("Brillant Toy", "Brillant Toy", brillantToyAssets, variantNameCondition(Collection, "Brillant Toy"))

    const coolBoxNanoAssets = createRuleConditionedPriceNodes(catalog, "Cool Box Nano", 5, {configGroup: Colors, rules})
    const coolBoxNanoContainer = createPriceContainer("Cool Box Nano", "Cool Box Nano", coolBoxNanoAssets, variantNameCondition(Collection, "Cool Box Nano"))

    const coolBoxMiniAssets = createRuleConditionedPriceNodes(catalog, "Cool Box Mini", 2, {configGroup: Colors, rules})
    const coolBoxMiniContainer = createPriceContainer("Cool Box Mini", "Cool Box Mini", coolBoxMiniAssets, variantNameCondition(Collection, "Cool Box Mini"))

    //TODO: catalog: tempo s, configurator: tempo small
    const tempoSmallAssets = createRuleConditionedPriceNodes(catalog, "Tempo S", 2, {configGroup: Colors, rules})
    const tempoSmallContainer = createPriceContainer("Tempo S", "Tempo S", tempoSmallAssets, variantNameCondition(Collection, "Tempo S"))

    const tempeteSmallAssets = createRuleConditionedPriceNodes(catalog, "Tempête Small", 7, {configGroup: Colors, rules})
    const tempeteSmallContainer = createPriceContainer("Tempête Small", "Tempête Small", tempeteSmallAssets, variantNameCondition(Collection, "Tempête Small"))

    const tempetePmAssets = createRuleConditionedPriceNodes(catalog, "Tempête P", 6, {configGroup: Colors, rules})
    const tempetePmContainer = createPriceContainer("Tempête PM", "Tempête PM", tempetePmAssets, variantNameCondition(Collection, "Tempête P"))

    //TODO: tempete MM - no materials
    const tempeteMMAssets = createRuleConditionedPriceNodes(catalog, "Tempête MM", 5, {configGroup: Colors, rules})
    const tempeteMMContainer = createPriceContainer("Tempête MM", "Tempête MM", tempeteMMAssets, variantNameCondition(Collection, "Tempête MM"))

    //TODO: Tempo L, 1 leather in catalog, 2 in configurator
    const brillantTempoLAssets = createRuleConditionedPriceNodes(catalog, "Brillant Tempo L", 1, {configGroup: Colors, rules})
    const brillantTempoLContainer = createPriceContainer(
        "Brillant Tempo L",
        "Brillant Tempo L",
        brillantTempoLAssets,
        variantNameCondition(Collection, "Brillant Tempo L"),
    )

    const leonceSmallAssets = createRuleConditionedPriceNodes(catalog, "Léonce S", 3, {configGroup: Colors, rules})
    const leonceSmallContainer = createPriceContainer("Léonce S", "Léonce S", leonceSmallAssets, variantNameCondition(Collection, "Léonce S"))

    const lingotAssets = createRuleConditionedPriceNodes(catalog, "Lingot", 5, {configGroup: Colors, rules}, ["Lingot S"])
    const lingotContainer = createPriceContainer("Lingot", "Lingot", lingotAssets, variantNameCondition(Collection, "Lingot", ["Lingot S"]))

    //TODO: no materials
    const lingotSAssets = createRuleConditionedPriceNodes(catalog, "Lingot S", 5, {configGroup: Colors, rules})
    const lingotSContainer = createPriceContainer("Lingot S", "Lingot S", lingotSAssets, variantNameCondition(Collection, "Lingot S"))

    const brillantMMAssets = createRuleConditionedPriceNodes(catalog, "Brillant MM", 5, {configGroup: Colors, rules})
    const brillantMMContainer = createPriceContainer("Brillant MM", "Brillant MM", brillantMMAssets, variantNameCondition(Collection, "Brillant MM"))

    const capriceAssets = createRuleConditionedPriceNodes(catalog, "Caprice", 3, {configGroup: Colors, rules}, ["Caprice Toy"])
    const capriceContainer = createPriceContainer("Caprice", "Caprice", capriceAssets, variantNameCondition(Collection, "Caprice", ["Caprice Toy"]))

    const capriceToyAssets = createRuleConditionedPriceNodes(catalog, "Caprice Toy", 3, {configGroup: Colors, rules})
    const capriceToyContainer = createPriceContainer("Caprice Toy", "Caprice Toy", capriceToyAssets, variantNameCondition(Collection, "Caprice Toy"))

    const leonceAssets = createRuleConditionedPriceNodes(catalog, "Léonce", 3, {configGroup: Colors, rules}, ["Holder", "Wallet", "Small"])
    const leonceContainer = createPriceContainer("Léonce", "Léonce", leonceAssets, variantNameCondition(Collection, "Léonce", ["Holder", "Wallet", "Small"]))

    const pinToyEclatAssets = createRuleConditionedPriceNodes(catalog, "Pin Toy Eclat", 2, {configGroup: Colors, rules})
    const pinToyEclatContainer = createPriceContainer("Pin Toy Eclat", "Pin Toy Eclat", pinToyEclatAssets, variantNameCondition(Collection, "Pin Toy Eclat"))

    //-
    const tempeteCityAssets = createRuleConditionedPriceNodes(catalog, "Tempête City", 3, {configGroup: Colors, rules})
    const tempeteCityContainer = createPriceContainer("Tempête City", "Tempête City", tempeteCityAssets, variantNameCondition(Collection, "Tempête City"))

    const tempeteLAssets = createRuleConditionedPriceNodes(catalog, "Tempête L", 5, {configGroup: Colors, rules})
    const tempeteLContainer = createPriceContainer("Tempête L", "Tempête L", tempeteLAssets, variantNameCondition(Collection, "Tempête L"))

    const tempeteToGoAssets = createRuleConditionedPriceNodes(catalog, "Tempête To Go", 3, {configGroup: Colors, rules})
    const tempeteToGoContainer = createPriceContainer("Tempête To Go", "Tempête To Go", tempeteToGoAssets, variantNameCondition(Collection, "Tempête To Go"))

    const tempeteXlAssets = createRuleConditionedPriceNodes(catalog, "Tempête XL", 2, {configGroup: Colors, rules})
    const tempeteXlContainer = createPriceContainer("Tempête XL", "Tempête XL", tempeteXlAssets, variantNameCondition(Collection, "Tempête XL"))

    //--- SLG

    const leonceCardHolderAssets = createRuleConditionedPriceNodes(catalog, "Léonce Card", 4, {configGroup: Colors, rules})
    const leonceCardHolderContainer = createPriceContainer(
        "Léonce Card Holder",
        "Léonce Card Holder",
        leonceCardHolderAssets,
        variantNameCondition(Collection, "Léonce Card Holder"),
    )

    //rev
    const gentlemenComeCardHolderAssets = createRuleConditionedPriceNodes(catalog, "Gentlemen Come Card Holder", 4, {configGroup: Colors, rules})
    const gentlemenComeCardHolderContainer = createPriceContainer(
        "Gentlemen Come Card Holder",
        "Gentlemen Come Card Holder",
        gentlemenComeCardHolderAssets,
        variantNameCondition(Collection, "Gentlemen Come Card Holder"),
    )

    const gentlemenLivreWalletAssets = createRuleConditionedPriceNodes(catalog, "Gentlemen Livre Wallet", 4, {configGroup: Colors, rules}, ["Mini"])
    const gentlemenLivreWalletContainer = createPriceContainer(
        "Gentlemen Livre Wallet",
        "Gentlemen Livre Wallet",
        gentlemenLivreWalletAssets,
        variantNameCondition(Collection, "Gentlemen Livre Wallet", ["Mini"]),
    )

    const gentlemenLivreWalletMiniAssets = createRuleConditionedPriceNodes(catalog, "Gentlemen Livre Wallet Mini", 4, {configGroup: Colors, rules})
    const gentlemenLivreWalletMiniContainer = createPriceContainer(
        "Gentlemen Livre Wallet Mini",
        "Gentlemen Livre Wallet Mini",
        gentlemenLivreWalletMiniAssets,
        variantNameCondition(Collection, "Gentlemen Livre Wallet Mini"),
    )

    const leonceLongWalletAssets = createRuleConditionedPriceNodes(catalog, "Léonce Long Wallet", 4, {configGroup: Colors, rules})
    const leonceLongWalletContainer = createPriceContainer(
        "Léonce Long Wallet",
        "Léonce Long Wallet",
        leonceLongWalletAssets,
        variantNameCondition(Collection, "Léonce Long Wallet"),
    )

    const leoncePassportHolderAssets = createRuleConditionedPriceNodes(catalog, "Léonce Passport Holder", 4, {configGroup: Colors, rules})
    const leoncePassportHolderContainer = createPriceContainer(
        "Léonce Passport Holder",
        "Léonce Passport Holder",
        leoncePassportHolderAssets,
        variantNameCondition(Collection, "Léonce Passport Holder"),
    )

    const leonceCompactWalletAssets = createRuleConditionedPriceNodes(catalog, "Léonce Compact Wallet", 4, {configGroup: Colors, rules})
    const leonceCompactWalletContainer = createPriceContainer(
        "Léonce Compact Wallet",
        "Léonce Compact Wallet",
        leonceCompactWalletAssets,
        variantNameCondition(Collection, "Léonce Compact Wallet"),
    )

    result.priceContainers = [
        //bags below
        brillantPmContainer,
        brillantMiniContainer,
        brillantToyContainer,
        coolBoxNanoContainer,
        coolBoxMiniContainer,
        tempoSmallContainer,
        tempeteSmallContainer,
        tempetePmContainer,
        tempeteMMContainer,
        brillantTempoLContainer,
        leonceSmallContainer,
        lingotContainer,
        lingotSContainer,
        brillantMMContainer,
        capriceContainer,
        capriceToyContainer,
        leonceContainer,
        pinToyEclatContainer,
        tempeteCityContainer,
        tempeteLContainer,
        tempeteToGoContainer,
        tempeteXlContainer,
        //slg below
        leonceCardHolderContainer,
        gentlemenComeCardHolderContainer,
        gentlemenLivreWalletContainer,
        gentlemenLivreWalletMiniContainer,
        leonceLongWalletContainer,
        leoncePassportHolderContainer,
        leonceCompactWalletContainer,
    ]

    return result
}
